import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError, errorText, type ShoppingItemDto, type ShoppingListSnapshotDto, type StoreSectionDto } from "../api/client";
import { formatQuantity, parseQuantity, relativeTime } from "../format";
import { discardPending, enqueueShopping, flushShopping, listPending, loadSnapshot, onQueueChange, saveSnapshot, type PendingShopping } from "../offline/queue";
import { noteRevision } from "../realtime/realtime";

const UNITS = ["", "Stück", "Packung", "kg", "g", "l", "ml", "Flasche", "Dose", "Glas", "Bund", "Becher", "Beutel"];

/**
 * Einkaufsliste: jede Aktion wird zuerst mit stabiler Request-ID vorgemerkt und dann übertragen.
 * Ohne Verbindung bleibt sie sichtbar als „nicht synchronisiert“ und wird beim erneuten Öffnen bzw.
 * bei Netzrückkehr nachgeholt. Konflikte werden angezeigt, nicht überschrieben.
 */
export function ShoppingPage() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<PendingShopping[]>([]);
  const [offlineCopy, setOfflineCopy] = useState<{ savedAt: string; data: ShoppingListSnapshotDto } | null>(null);
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unit, setUnit] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [moving, setMoving] = useState<ShoppingItemDto | null>(null);
  const [showAmount, setShowAmount] = useState(false);
  const [removed, setRemoved] = useState<ShoppingItemDto | null>(null);
  const [undoing, setUndoing] = useState(false);

  const list = useQuery({
    queryKey: ["shopping", "default"],
    queryFn: async () => {
      const r = await api<ShoppingListSnapshotDto>("/api/v1/shopping-lists/default");
      noteRevision(r.revision);
      void saveSnapshot("shopping:default", r);
      return r;
    },
  });

  useEffect(() => {
    const refresh = () => void listPending().then(setPending);
    refresh();
    return onQueueChange(refresh);
  }, []);

  useEffect(() => {
    if (list.isError && list.error instanceof ApiError && list.error.isOffline) {
      void loadSnapshot<ShoppingListSnapshotDto>("shopping:default").then((s) => setOfflineCopy(s ?? null));
    } else if (list.data) {
      setOfflineCopy(null);
    }
  }, [list.isError, list.error, list.data]);

  const data = list.data ?? offlineCopy?.data;
  const listId = data?.list.id ?? "default";

  const run = async (commands: Parameters<typeof enqueueShopping>[1]) => {
    const entry = await enqueueShopping(listId, commands);
    const result = await flushShopping();
    if (result.stoppedOffline) setNotice("Offline – Änderung wird übertragen, sobald wieder eine Verbindung besteht.");
    else setNotice(null);
    await qc.invalidateQueries({ queryKey: ["shopping"] });
    await qc.invalidateQueries({ queryKey: ["overview"] });
    return result.completed.find(c => c.requestId === entry.id)?.result;
  };
  const remove = async (item: ShoppingItemDto) => {
    try {
      const response = await run([{ type: "remove", itemId: item.id, expectedVersion: item.version }]);
      const saved = response?.results.find(r => r.item.id === item.id)?.item;
      if (saved) setRemoved(saved);
    } catch (e) { setNotice(errorText(e)); }
  };
  const undoRemove = async () => {
    if (!removed || undoing) return;
    setUndoing(true);
    try {
      await run([{ type: "uncheck", itemId: removed.id, expectedVersion: removed.version }]);
      setRemoved(null);
    } catch (e) { setNotice(errorText(e)); }
    finally { setUndoing(false); }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    // Anzahl immer gepflegt: leer = 1, ungültig = Hinweis statt still raten.
    const q = parseQuantity(quantity);
    if (q === null) {
      setNotice("Bitte eine gültige Anzahl eingeben (z. B. 1, 2 oder 0,5).");
      return;
    }
    const command = { type: "add", name: name.trim(), quantity: q, unit: unit || null };
    // Sofort leeren: wer schnell den nächsten Artikel tippt, verliert ihn nicht, wenn die Übertragung später fertig wird.
    setName("");
    setQuantity("1");
    setUnit("");
    setShowAmount(false);
    await run([command]);
  };

  const pendingStatus = new Map<string, string>();
  const pendingAdds: { key: string; name: string; quantity: number | null; unit: string | null; conflict?: string }[] = [];
  for (const p of pending) {
    for (const c of p.commands) {
      if (c.type === "add") pendingAdds.push({ key: p.id + c.name, name: c.name ?? "", quantity: (c.quantity as number | null) ?? null, unit: c.unit ?? null, conflict: p.state === "conflict" ? p.error : undefined });
      else if (c.itemId) pendingStatus.set(c.itemId, c.type);
    }
  }

  const items = data?.list.items ?? [];
  const open = items.filter((i) => effectiveStatus(i, pendingStatus) === "open");
  const done = items.filter((i) => effectiveStatus(i, pendingStatus) === "purchased");
  const sections = data?.list.sections ?? [];
  // Offene Artikel nach Abteilung in Laufrichtung des Markts (Eingang → Kasse); unbekannte Abteilungen ans Ende.
  const groups = [...sections, { key: "", label: "Weitere" }]
    .map((s) => ({ section: s, items: open.filter((i) => (sections.some((x) => x.key === i.category) ? i.category === s.key : s.key === "")) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="page">
      <h1>{data?.list.name ?? "Einkauf"}</h1>
      {offlineCopy && <p className="warn">Offline – gespeicherter Stand von {relativeTime(offlineCopy.savedAt)}.</p>}
      {notice && <p className="warn">{notice}</p>}
      {removed && <div className="undo-notice" role="status"><span>{removed.name} entfernt</span><button className="btn" disabled={undoing} onClick={() => void undoRemove()}>Rückgängig</button><button className="btn ghost" aria-label="Hinweis schließen" onClick={() => setRemoved(null)}>×</button></div>}
      {/* Eine Zeile: Anzahl (immer, vorbelegt 1) + Artikel + Hinzufügen; Einheit nur bei Bedarf. */}
      <form className="add-item" onSubmit={add}>
        <input value={quantity} onChange={(e) => setQuantity(e.target.value)} onFocus={(e) => e.target.select()} aria-label="Anzahl"
          title="Anzahl (leer = 1)" placeholder="1" inputMode="decimal" className="qty" maxLength={7} />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Artikel hinzufügen" aria-label="Artikel" maxLength={200} enterKeyHint="done" />
        <button type="button" className={`btn ghost amount-toggle${showAmount || unit ? " on" : ""}`} aria-expanded={showAmount}
          aria-label="Einheit angeben" title="Einheit (Packung, kg …)" onClick={() => setShowAmount(!showAmount)}>{unit || "Einheit"}</button>
        <button className="btn primary" aria-label="Hinzufügen" title="Hinzufügen">+</button>
        {showAmount && (
          <div className="amount">
            <select value={unit} onChange={(e) => setUnit(e.target.value)} aria-label="Einheit">
              {UNITS.map((u) => <option key={u} value={u}>{u || "Stück (×)"}</option>)}
            </select>
          </div>
        )}
      </form>

      {pending.filter((p) => p.state === "conflict").map((p) => (
        <div key={p.id} className="warn row between">
          <span>Konflikt: {p.error}</span>
          <button className="btn ghost" onClick={() => discardPending(p.id)}>Verwerfen</button>
        </div>
      ))}

      <ul className="shopping">
        {pendingAdds.map((a) => (
          <li key={a.key} className="pending">
            <span className="box" aria-hidden="true">☐</span>
            <span className="name">{a.name}</span>
            <span className="qty">{formatQuantity(a.quantity, a.unit)}</span>
            <span className="badge warn-badge">{a.conflict ? "Konflikt" : "nicht synchronisiert"}</span>
          </li>
        ))}
      </ul>
      {groups.map((g) => (
        <section key={g.section.key} className="shop-section" aria-label={g.section.label}>
          <h3>{g.section.label} <span className="muted">· {g.items.length}</span></h3>
          <ul className="shopping">
            {g.items.map((i) => (
              <ItemRow key={i.id} item={i} pending={pendingStatus.has(i.id)} onToggle={() => run([{ type: "check", itemId: i.id, expectedVersion: i.version }])}
                onRemove={() => void remove(i)} onMove={sections.length > 0 ? () => setMoving(i) : undefined} />
            ))}
          </ul>
        </section>
      ))}
      {moving && (
        <SectionPicker
          item={moving}
          sections={sections}
          onClose={() => setMoving(null)}
          onPick={(key) => {
            setMoving(null);
            void run([{ type: "update", itemId: moving.id, expectedVersion: moving.version, category: key }]);
          }}
        />
      )}
      {open.length === 0 && pendingAdds.length === 0 && <p className="muted">Die Liste ist leer.</p>}
      {open.length > 0 && sections.length > 0 && <p className="hint small">Falsche Abteilung? „Abteilung“ antippen oder Artikel lange drücken.</p>}
      {done.length > 0 && (
        <>
          <h2>Kürzlich erledigt</h2>
          <ul className="shopping done">
            {done.map((i) => (
              <ItemRow key={i.id} item={i} pending={pendingStatus.has(i.id)} checked onToggle={() => run([{ type: "uncheck", itemId: i.id, expectedVersion: i.version }])} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function effectiveStatus(item: ShoppingItemDto, pending: Map<string, string>): string {
  const p = pending.get(item.id);
  if (p === "check") return "purchased";
  if (p === "uncheck") return "open";
  if (p === "remove") return "removed";
  return item.status;
}

function ItemRow({ item, checked, pending, onToggle, onRemove, onMove }: { item: ShoppingItemDto; checked?: boolean; pending: boolean; onToggle: () => void; onRemove?: () => void; onMove?: () => void }) {
  // Lange drücken (Touch) öffnet die Abteilungswahl; mit Maus/Tastatur gibt es den ⇅-Knopf.
  const timer = useRef<number | undefined>(undefined);
  const start = () => {
    if (!onMove) return;
    timer.current = window.setTimeout(() => {
      navigator.vibrate?.(15);
      onMove();
    }, 550);
  };
  const stop = () => window.clearTimeout(timer.current);
  return (
    <li className={pending ? "pending" : ""} onTouchStart={start} onTouchEnd={stop} onTouchMove={stop} onTouchCancel={stop} onContextMenu={(e) => onMove && e.preventDefault()}>
      <button className="box" onClick={onToggle} aria-label={checked ? `${item.name} wieder öffnen` : `${item.name} abhaken`}>{checked ? "☑" : "☐"}</button>
      <span className="name">{item.name}</span>
      <span className="qty">{formatQuantity(item.quantity as number | null, item.unit)}</span>
      {pending && <span className="badge warn-badge">nicht synchronisiert</span>}
      {onMove && <button className="btn ghost move" onClick={onMove} aria-label={`Abteilung von ${item.name} ändern`} title="Abteilung ändern">Abteilung</button>}
      {onRemove && <button className="btn ghost" onClick={onRemove} aria-label={`${item.name} entfernen`}>✕</button>}
    </li>
  );
}

/** Abteilung korrigieren; der Server merkt sich die Korrektur für denselben Artikel. */
function SectionPicker({ item, sections, onPick, onClose }: { item: ShoppingItemDto; sections: StoreSectionDto[]; onPick: (key: string) => void; onClose: () => void }) {
  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label={`Abteilung für ${item.name}`} onClick={onClose}>
      <div className="dialog card" onClick={(e) => e.stopPropagation()}>
        <h2>Wo liegt „{item.name}“?</h2>
        <p className="hint small">Wird für diesen Artikel gemerkt.</p>
        <div className="section-picker">
          {sections.map((s) => (
            <button key={s.key} className={`btn${s.key === item.category ? " primary" : ""}`} onClick={() => onPick(s.key)}>{s.label}</button>
          ))}
        </div>
        <div className="row between"><span /><button className="btn" onClick={onClose}>Abbrechen</button></div>
      </div>
    </div>
  );
}
